<script setup lang="ts">
import { computed, ref } from 'vue'
import { UserProcessingState } from '@roadfootage/api-client'
import type { CreatorFileProcessingResponse } from '@roadfootage/api-client'
import { supportEmail } from '@/config/support'
import { formatNumber } from '@/utils/format'

// Uploader-facing processing progress. Deliberately plain: it only ever shows a percent, Processing,
// Ready or Couldn't process, never a reason. The API doesn't send one either.
const props = defineProps<{
  files: CreatorFileProcessingResponse[] | undefined
  /** The report was rejected in moderation. */
  rejected?: boolean
  /** Files that are not still processing can be removed (the report is pending and has more than one file). */
  removable?: boolean
  /** The file being removed right now, if any. */
  removingFileId?: string | null
}>()

const emit = defineEmits<{ remove: [fileId: string] }>()

const confirming = ref<CreatorFileProcessingResponse | null>(null)
const confirmOpen = computed({
  get: () => confirming.value !== null,
  set: (open: boolean) => {
    if (!open) confirming.value = null
  }
})

const canRemove = (file: CreatorFileProcessingResponse): boolean =>
  !!props.removable && file.state !== UserProcessingState.Processing

const confirmRemove = () => {
  const fileId = confirming.value?.fileId
  confirming.value = null
  if (fileId) emit('remove', fileId)
}

const mailto = `mailto:${supportEmail}`
const hasFailure = computed(() =>
  (props.files ?? []).some((f) => f.state === UserProcessingState.CouldNotProcess)
)

const label = (file: CreatorFileProcessingResponse): string => {
  if (file.state === UserProcessingState.Ready) return 'Ready'
  if (file.state === UserProcessingState.CouldNotProcess) return "Couldn't process"
  return `Processing ${formatNumber(file.percent ?? 0)}%`
}
</script>

<template>
  <div v-if="files?.length || rejected" data-testid="file-processing">
    <div
      v-for="file in files ?? []"
      :key="file.fileId"
      class="q-mb-xs"
      data-testid="file-processing-item"
    >
      <div class="row items-center no-wrap">
        <q-icon
          v-if="file.state === UserProcessingState.Ready"
          name="mdi-check-circle"
          color="positive"
          size="xs"
          class="q-mr-xs"
        />
        <q-icon
          v-else-if="file.state === UserProcessingState.CouldNotProcess"
          name="mdi-alert-circle-outline"
          color="negative"
          size="xs"
          class="q-mr-xs"
        />
        <div class="text-caption text-blue-grey-7 ellipsis col" :title="file.fileName ?? undefined">
          {{ file.fileName }}
        </div>
        <div
          class="text-caption text-weight-medium q-ml-sm"
          :class="file.state === UserProcessingState.CouldNotProcess ? 'text-negative' : ''"
          data-testid="file-processing-state"
        >
          {{ label(file) }}
        </div>
        <q-btn
          v-if="canRemove(file)"
          flat
          round
          dense
          size="sm"
          color="blue-grey-7"
          icon="mdi-delete-outline"
          class="q-ml-xs"
          :aria-label="`Remove ${file.fileName}`"
          :loading="removingFileId === file.fileId"
          :disable="!!removingFileId"
          data-testid="remove-file"
          @click="confirming = file"
        />
      </div>
      <div
        v-if="file.earlierReportId"
        class="text-caption text-blue-grey-8 q-mt-xs"
        data-testid="earlier-upload"
      >
        <q-icon name="mdi-alert-outline" color="warning" size="xs" class="q-mr-xs" />
        You've uploaded this clip before.
        <router-link :to="{ name: 'uploads', query: { report: file.earlierReportId } }">
          See earlier report
        </router-link>
      </div>
      <q-linear-progress
        v-if="file.state === UserProcessingState.Processing"
        aria-label="Processing progress"
        rounded
        size="6px"
        color="blue-8"
        track-color="blue-grey-2"
        :value="(file.percent ?? 0) / 100"
        class="q-mt-xs"
      />
    </div>

    <div
      v-if="hasFailure"
      class="text-caption text-blue-grey-8 q-mt-xs"
      data-testid="file-processing-help"
    >
      We couldn't process this file. Please upload it again, or contact us if it keeps happening.
      <div class="q-mt-xs">
        <q-btn
          flat
          dense
          size="sm"
          color="primary"
          icon="mdi-upload"
          label="Upload again"
          :to="{ name: 'upload' }"
          data-testid="file-processing-reupload"
        />
        <q-btn
          flat
          dense
          size="sm"
          color="primary"
          icon="mdi-email-outline"
          label="Contact us"
          :href="mailto"
          data-testid="file-processing-contact"
        />
      </div>
    </div>

    <div
      v-if="rejected"
      class="text-caption text-blue-grey-8 q-mt-xs"
      data-testid="report-rejected"
    >
      This report wasn't approved. You can share new footage, or
      <a :href="mailto">contact us</a> if you'd like to know why.
    </div>

    <q-dialog v-model="confirmOpen">
      <q-card style="max-width: 400px; width: 100%">
        <q-card-section class="row items-start no-wrap">
          <q-icon name="mdi-alert-circle-outline" color="warning" size="sm" class="q-mr-sm" />
          <div>
            <div class="text-subtitle1 text-weight-bold">Remove {{ confirming?.fileName }}?</div>
            <div class="text-body2 q-mt-xs">
              It will be taken off this report. Our moderators won't see it.
            </div>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Cancel" v-close-popup data-testid="remove-file-cancel" />
          <q-btn
            unelevated
            color="negative"
            label="Remove"
            data-testid="remove-file-confirm"
            @click="confirmRemove"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>
